/**
 * Handles Word document events and coordinates state updates.
 *
 * This handler:
 * 1. Registers for Word document selection change events
 * 2. On selection change, calls the provided callback to push state
 *
 * Designed to work with WordRpcBridge.pushSurfaceContext().
 */
export class ChatDocumentEventHandler {
  private onStateChange: () => Promise<void>;
  private handlerRemover: (() => void) | null = null;
  private isSetup = false;

  constructor(onStateChange: () => Promise<void>) {
    this.onStateChange = onStateChange;
  }

  /**
   * Set up the event handler.
   * - Registers for document selection change events
   * - Sends initial document state
   */
  async setup(): Promise<void> {
    if (this.isSetup) {
      console.warn("ChatDocumentEventHandler already set up");
      return;
    }

    this.isSetup = true;

    // Register for selection change events
    await this.registerSelectionChangeHandler();

    // Send initial state
    await this.onStateChange();
  }

  /**
   * Clean up the event handler.
   * - Removes document event listeners
   */
  cleanup(): void {
    if (this.handlerRemover) {
      this.handlerRemover();
      this.handlerRemover = null;
    }
    this.isSetup = false;
  }

  /**
   * Force refresh and send current document state.
   * Called when iframe explicitly requests a refresh.
   */
  async refresh(): Promise<void> {
    await this.onStateChange();
  }

  /**
   * Register for Word document selection change events.
   */
  private async registerSelectionChangeHandler(): Promise<void> {
    return new Promise((resolve, reject) => {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        () => {
          // Selection changed - push new state
          void this.onStateChange();
        },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            // Store remover function for cleanup
            this.handlerRemover = () => {
              Office.context.document.removeHandlerAsync(
                Office.EventType.DocumentSelectionChanged,
                { handler: () => {} },
                () => {},
              );
            };
            resolve();
          } else {
            console.error("Failed to register selection change handler:", result.error);
            reject(result.error);
          }
        },
      );
    });
  }
}
